import { useEffect } from 'react';
import { useSiteSettings, usePageSeo } from '@/hooks/useSiteSettings';

interface SEOHeadProps {
  path: string;
  // Optional overrides for dynamic pages
  title?: string;
  description?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
}

export function SEOHead({ 
  path,
  title: titleOverride,
  description: descriptionOverride,
  ogTitle: ogTitleOverride,
  ogDescription: ogDescriptionOverride,
  ogImage: ogImageOverride,
}: SEOHeadProps) {
  const { data: siteSettings } = useSiteSettings();
  const { data: pageSeo } = usePageSeo(path);

  useEffect(() => {
    if (!siteSettings) return;

    const { seo, organization } = siteSettings;
    const baseUrl = organization?.url || 'https://automotiveapi.io';
    
    // Priority: props override > page_seo > site defaults
    const title = titleOverride || pageSeo?.title || seo?.default_title || 'AutomotiveAPI';
    const description = descriptionOverride || pageSeo?.description || seo?.default_description || '';
    const ogTitle = ogTitleOverride || pageSeo?.og_title || pageSeo?.title || seo?.default_title || title;
    const ogDescription = ogDescriptionOverride || pageSeo?.og_description || pageSeo?.description || seo?.default_description || description;
    const ogImage = ogImageOverride || pageSeo?.og_image || seo?.default_og_image || '';
    const canonicalUrl = pageSeo?.canonical_url || `${baseUrl}${path}`;
    const twitterHandle = seo?.twitter_handle || '';

    // Update document title
    document.title = title;

    // Helper to set or update meta tags
    const setMeta = (name: string, content: string, isProperty = false) => {
      if (!content) return;
      const attr = isProperty ? 'property' : 'name';
      let meta = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement;
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute(attr, name);
        document.head.appendChild(meta);
      }
      meta.content = content;
    };

    // Helper to set or update link tags
    const setLink = (rel: string, href: string) => {
      if (!href) return;
      let link = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement;
      if (!link) {
        link = document.createElement('link');
        link.rel = rel;
        document.head.appendChild(link);
      }
      link.href = href;
    };

    // Standard meta tags
    setMeta('description', description);
    setMeta('author', organization?.name || 'AutomotiveAPI');

    // Open Graph tags
    setMeta('og:title', ogTitle, true);
    setMeta('og:description', ogDescription, true);
    setMeta('og:type', 'website', true);
    setMeta('og:url', canonicalUrl, true);
    if (ogImage) {
      setMeta('og:image', ogImage, true);
    }
    setMeta('og:site_name', seo?.site_name || 'AutomotiveAPI', true);

    // Twitter Card tags
    setMeta('twitter:card', 'summary_large_image');
    if (twitterHandle) {
      setMeta('twitter:site', twitterHandle);
    }
    setMeta('twitter:title', ogTitle);
    setMeta('twitter:description', ogDescription);
    if (ogImage) {
      setMeta('twitter:image', ogImage);
    }

    // Canonical URL
    setLink('canonical', canonicalUrl);

    // JSON-LD structured data
    const existingJsonLd = document.querySelector('script[data-seo-jsonld]');
    if (existingJsonLd) {
      existingJsonLd.remove();
    }

    const jsonLdScript = document.createElement('script');
    jsonLdScript.type = 'application/ld+json';
    jsonLdScript.setAttribute('data-seo-jsonld', 'true');

    const jsonLdData: Record<string, unknown>[] = [];

    // Organization schema
    if (organization) {
      const orgSchema: Record<string, unknown> = {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: organization.name,
        url: organization.url,
      };
      
      if (organization.legal_name) {
        orgSchema.legalName = organization.legal_name;
      }
      if (organization.logo_url) {
        orgSchema.logo = organization.logo_url;
      }
      if (organization.founding_date) {
        orgSchema.foundingDate = organization.founding_date;
      }
      if (organization.description) {
        orgSchema.description = organization.description;
      }
      if (organization.contact_email) {
        orgSchema.email = organization.contact_email;
      }
      
      const sameAs: string[] = [];
      if (organization.social_links?.twitter) sameAs.push(organization.social_links.twitter);
      if (organization.social_links?.linkedin) sameAs.push(organization.social_links.linkedin);
      if (organization.social_links?.github) sameAs.push(organization.social_links.github);
      if (sameAs.length > 0) {
        orgSchema.sameAs = sameAs;
      }
      
      jsonLdData.push(orgSchema);
    }

    // WebSite schema
    jsonLdData.push({
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: seo?.site_name || 'AutomotiveAPI',
      url: baseUrl,
    });

    // WebPage schema
    const webPageSchema: Record<string, unknown> = {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: title,
      description: description,
      url: canonicalUrl,
    };

    // Add custom JSON-LD from page if available
    if (pageSeo?.json_ld) {
      Object.assign(webPageSchema, pageSeo.json_ld);
    }

    jsonLdData.push(webPageSchema);

    jsonLdScript.textContent = JSON.stringify(jsonLdData);
    document.head.appendChild(jsonLdScript);

    // Cleanup function
    return () => {
      const scriptToRemove = document.querySelector('script[data-seo-jsonld]');
      if (scriptToRemove) {
        scriptToRemove.remove();
      }
    };
  }, [siteSettings, pageSeo, path, titleOverride, descriptionOverride, ogTitleOverride, ogDescriptionOverride, ogImageOverride]);

  return null;
}
